import React, { useMemo } from "react";
import { Text, Card, Group, Divider, Radio } from "@mantine/core";
import { ImageWithBoundingBox } from "@labeling-tools/ui-components";
import {
  JobTaskDetailSchema,
  OoSParcelSchema,
} from "@augmodo/lebron-browser-client";
import { TaskCounter } from "./TaskCounter";
import { QuantitySlider } from "./QuantitySlider";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { RootState } from "../store";
import { ActionStatusBadge } from "./ActionStatusBadge";
import { QaQuestionAction } from "./QaQuestionAction";
import { ViewMode } from "@/types";
import { Carousel } from "@mantine/carousel";
import { uniq } from "lodash";
import { PlanogramModal } from "./modals/PlanogramModal";
import { ProductSuggestions } from "./ProductSuggestions";
import { EmptyAreaCard } from "./EmptyAreaCard";
import { setPlanogramModalSource, setPlanogramModalUsed } from "@/store";
import {
  getNoMatchNeeded,
  getMainCropPoints,
  getReferenceCropPoints,
} from "../utils/helpers";
import { PlanogramOnlyWorkflow } from "./modals/PlanogramOnlyWorkflow";
import { useTaskPlanogram } from "@/hooks/useTaskPlanogram";
import { useItemDetectionQuestion } from "@/hooks/useItemDetectionQuestion";

interface ItemVerificationProps {
  missingItemImageUrl: string | undefined;
  foundItemImageUrl: string | undefined;
  task: JobTaskDetailSchema;
  mode?: ViewMode;
  referenceImageCount?: number;
  parcel: OoSParcelSchema;
  // events: any[];
}

export const ItemVerification: React.FC<ItemVerificationProps> = ({
  missingItemImageUrl,
  foundItemImageUrl,
  task,
  mode,
  referenceImageCount,
  parcel,
  // events,
}) => {
  const dispatch = useAppDispatch();
  const itemDetectionConfirmed = useAppSelector(
    (state: RootState) => state.oos.itemDetectionConfirmed,
  );
  const planogramSelectedProductId = useAppSelector(
    (state: RootState) => state.oos.planogramSelectedProductId,
  );
  const referenceImage = (task as any).data?.reference_images[
    task.data?.reference_image_index as number
  ];
  const productIdsTopN = uniq(
    referenceImage?.product_ids_top_n ?? [],
  ) as string[];
  const emptyAreaConfirmed = useAppSelector(
    (state: RootState) => state.oos.emptyAreaConfirmed,
  );
  const resultMetadata = useAppSelector(
    (state: RootState) => state.oos.resultMetadata,
  );
  const { planogramId, planogramData } = useTaskPlanogram(task, parcel);

  const {
    questionOneHasAnswer,
    showNoLocationRequired,
    handleItemDetectionConfirmedChange,
  } = useItemDetectionQuestion();

  const handleOpenPlanogramFromQ1 =
    planogramId && planogramData
      ? () => {
          dispatch(setPlanogramModalSource("empty_area"));
          dispatch(setPlanogramModalUsed(true));
        }
      : undefined;
  const foundItemPoints = useMemo(
    () => getReferenceCropPoints((task as any).data) ?? [{ points: [] }],
    [task],
  );
  const emptyAreaPoints = useMemo(
    () => getMainCropPoints((task as any).data) ?? [{ points: [] }],
    [task],
  );
  const itemVerificationResult =
    task.result?.item_detection_confirmed === true ||
    task.data?.confirmation_result?.item_detection_confirmed === true
      ? "yes"
      : "no";

  // Viewer-only: hide Q2/Q3 inputs and swap in "No … Required" placeholders
  // when the labeler's Q2/Q3 answers would be thrown out at submission.
  // Admin/QA never populate Redux, so both flags stay false there — those
  // modes keep rendering the image and rely on the UNANSWERED badge to
  // communicate the blocked state.
  const noMatchNeeded = getNoMatchNeeded(
    emptyAreaConfirmed,
    itemDetectionConfirmed,
    resultMetadata,
  );

  const productId = referenceImage?.product_id;

  const planogramOnlyView =
    !!task?.data?.identified_product_id ||
    !!(task?.data as any)?.box_draw_task_id;

  return (
    <div
      className="flex flex-col min-h-screen bg-gray-50"
      data-item-verification
    >
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center p-6 pb-24">
        <div className="w-full max-w-8xl">
          <div className="flex flex-col items-center mb-8 relative">
            {/* display which task number we're on */}
            {mode !== "admin" && mode !== "confirmation-qa-feedback" && (
              <TaskCounter taskId={task.id} />
            )}
          </div>

          {/* Planogram only view */}
          {/* used when we don't have reference images */}
          {planogramOnlyView ? (
            <PlanogramOnlyWorkflow
              parcel={parcel}
              storeId={task?.data?.store?.id}
              task={task}
              mode={mode}
              planogramSelectedProductId={planogramSelectedProductId}
              capturedImage={
                <>
                  <EmptyAreaCard
                    missingItemImageUrl={missingItemImageUrl}
                    emptyAreaPoints={emptyAreaPoints}
                    mode={mode}
                    task={task}
                  />
                  <QuantitySlider
                    referenceImageCount={referenceImageCount || 0}
                    disableBtns={
                      questionOneHasAnswer ||
                      itemDetectionConfirmed !== null ||
                      referenceImageCount === 0
                    }
                  />
                </>
              }
            />
          ) : (
            /* Standard view */
            <Group className="mb-8" style={{ alignItems: "unset", gap: 10 }}>
              <div className="w-[calc(66.67%-10px)]">
                <div className="flex gap-[10px]">
                  <Carousel
                    className="flex-1"
                    withControls={false}
                    withIndicators={false}
                    slidesToScroll={2}
                    align="start"
                    slideSize="50%"
                    slideGap="10px"
                    draggable={false}
                  >
                    <Carousel.Slide>
                      <EmptyAreaCard
                        missingItemImageUrl={missingItemImageUrl}
                        emptyAreaPoints={emptyAreaPoints}
                        mode={mode}
                        task={task}
                        onOpenPlanogram={handleOpenPlanogramFromQ1}
                      />
                    </Carousel.Slide>
                    <Carousel.Slide>
                      <Card
                        shadow="lg"
                        padding="lg"
                        radius="md"
                        withBorder
                        className="flex-1"
                        style={{
                          opacity:
                            !questionOneHasAnswer && mode === "viewer"
                              ? 0.3
                              : 1,
                        }}
                      >
                        <div className="flex flex-col gap-2">
                          <Text
                            size="md"
                            fw={500}
                            className="mb-4 text-gray-700 text-left"
                            style={{ marginBottom: 0 }}
                          >
                            Product Location Detected
                          </Text>
                          {!showNoLocationRequired && (
                            <ImageWithBoundingBox
                              src={foundItemImageUrl}
                              alt="Found Item"
                              points={foundItemPoints}
                              variant="proposal"
                              modelPredictions={[0]}
                              clickable={mode !== "admin"}
                              enablePulsing={true}
                              crop={true}
                            />
                          )}
                          <Divider style={{ margin: "5px 0 10px" }} />
                          <div
                            className="flex flex-col"
                            style={{ marginTop: "auto" }}
                          >
                            {!showNoLocationRequired && (
                              <Text
                                size="md"
                                fw={500}
                                className="text-left"
                                style={{ marginBottom: "10px" }}
                              >
                                Is the blue box in the same location as the red
                                box?
                              </Text>
                            )}
                            {showNoLocationRequired ? (
                              <div className="flex items-center justify-center bg-gray-100 p-4 rounded-md h-[100px] mt-2">
                                <Text size="md" className="text-gray-700">
                                  No Product Location Required
                                </Text>
                              </div>
                            ) : (
                              <Radio.Group
                                onChange={handleItemDetectionConfirmedChange}
                                value={
                                  itemDetectionConfirmed === true
                                    ? "yes"
                                    : itemDetectionConfirmed === false
                                      ? "no"
                                      : typeof task.result
                                            ?.item_detection_confirmed ===
                                            "boolean" ||
                                          typeof task.data?.confirmation_result
                                            ?.item_detection_confirmed ===
                                            "boolean"
                                        ? itemVerificationResult
                                        : null
                                }
                              >
                                <Group mb="sm">
                                  <Radio
                                    value="yes"
                                    label="Yes"
                                    disabled={
                                      mode !== "viewer" || !questionOneHasAnswer
                                    }
                                  />
                                  <Radio
                                    value="no"
                                    label="No"
                                    disabled={
                                      mode !== "viewer" || !questionOneHasAnswer
                                    }
                                  />
                                </Group>
                              </Radio.Group>
                            )}

                            {/* "admin" mode shows action status */}
                            {mode === "admin" && (
                              <ActionStatusBadge
                                task={task}
                                resultField="item_detection_confirmed"
                              />
                            )}

                            {/* "confirmation-qa" mode shows action status from data */}
                            {(mode === "confirmation-qa" ||
                              mode === "confirmation-qa-feedback") && (
                              <>
                                <ActionStatusBadge
                                  task={{
                                    ...task,
                                    result:
                                      task.data?.confirmation_result ||
                                      ({} as any),
                                  }}
                                  resultField="item_detection_confirmed"
                                />
                                <QaQuestionAction
                                  questionNumber={2}
                                  task={task}
                                />
                              </>
                            )}
                          </div>
                        </div>
                      </Card>
                    </Carousel.Slide>
                  </Carousel>
                </div>
                <QuantitySlider
                  referenceImageCount={referenceImageCount || 0}
                  disableBtns={
                    questionOneHasAnswer ||
                    itemDetectionConfirmed !== null ||
                    referenceImageCount === 0
                  }
                />
              </div>
              <div className="w-[calc(33.33%-10px)]">
                <ProductSuggestions
                  questionOneHasAnswer={questionOneHasAnswer}
                  mode={mode}
                  task={task}
                  productIdsTopN={productIdsTopN}
                  productId={productId}
                  planogramId={planogramId}
                  noMatchNeeded={noMatchNeeded}
                />
              </div>
            </Group>
          )}
        </div>
      </div>

      {/* Planogram modal in standard view */}
      {/* this pops up when a user clicks the "p" icon on a product card or the empty area card */}
      {!!task && !planogramOnlyView && !!planogramData && (
        <PlanogramModal
          planogramData={planogramData}
          storeId={task?.data?.store?.id}
          task={task}
          mode={mode}
          planogramSelectedProductId={planogramSelectedProductId}
          zoomToProductId={productIdsTopN[0]}
          capturedImage={
            <ImageWithBoundingBox
              src={foundItemImageUrl}
              alt="Found Item"
              points={foundItemPoints}
              variant="proposal"
              modelPredictions={[0]}
              enablePulsing={true}
              crop={true}
            />
          }
          emptyAreaImage={
            <ImageWithBoundingBox
              src={missingItemImageUrl}
              alt="Missing Item"
              points={emptyAreaPoints}
              variant="empty"
              enablePulsing={true}
              crop={true}
              clickable={mode !== "admin"}
            />
          }
        />
      )}
    </div>
  );
};
